import { Adversary } from './types';

export const coreAdversaries: Adversary[] = [
  {
    name: `Giant Rat`,
    tier: 1,
    role: `Minion`,
    description: `A cat-sized rodent skilled at scavenging and survival.`,
    motives: `Burrow, hunger, scavenge, wear down`,
    difficulty: 10,
    hp: 1,
    stress: 1,
    atk: { modifier: `–4`, name: `Claws`, range: `Melee`, damage: `1 phy` },
    experience: `Keen Senses +3`,
    features: [
      { name: `Minion (3)`, type: `Passive`, text: `The Rat is defeated when they take any damage. For every 3 damage a PC deals to the Rat, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Giant Rats within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 1 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Rotted Zombie`,
    tier: 1,
    role: `Minion`,
    description: `A decaying corpse ambling toward their prey.`,
    motives: `Eat flesh, hunger, maul, surround`,
    difficulty: 8,
    hp: 1,
    stress: 1,
    atk: { modifier: `–3`, name: `Bite`, range: `Melee`, damage: `2 phy` },
    features: [
      { name: `Minion (3)`, type: `Passive`, text: `The Zombie is defeated when they take any damage. For every 3 damage a PC deals to the Zombie, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Rotted Zombies within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 2 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Skeleton Dredge`,
    tier: 1,
    role: `Minion`,
    description: `A clattering pile of bones.`,
    motives: `Fall apart, overwhelm, play dead, steal skin`,
    difficulty: 8,
    hp: 1,
    stress: 1,
    atk: { modifier: `–1`, name: `Bone Claws`, range: `Melee`, damage: `1 phy` },
    features: [
      { name: `Minion (4)`, type: `Passive`, text: `The Dredge is defeated when they take any damage. For every 4 damage a PC deals to the Dredge, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Dredges within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 1 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Tangle Bramble`,
    tier: 1,
    role: `Minion`,
    description: `An animate, blood-drinking tumbleweed.`,
    motives: `Combine, drain, entangle`,
    difficulty: 11,
    hp: 1,
    stress: 1,
    atk: { modifier: `–1`, name: `Thorns`, range: `Melee`, damage: `2 phy` },
    features: [
      { name: `Minion (4)`, type: `Passive`, text: `The Bramble is defeated when they take any damage. For every 4 damage a PC deals to the Tangle Bramble, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Tangle Brambles within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 2 physical damage each. Combine this damage.` },
      { name: `Drain and Multiply`, type: `Reaction`, text: `When an attack from the Bramble causes a target to mark HP and there are three or more Tangle Bramble Minions within Close range, you can combine the Minions into a Tangle Bramble Swarm Horde. The Horde's HP is equal to the number of Minions combined.` },
    ],
  },
  {
    name: `Jagged Knife Lackey`,
    tier: 1,
    role: `Minion`,
    description: `A thief with simple clothes and small daggers, eager to prove themselves.`,
    motives: `Escape, profit, throw smoke`,
    difficulty: 9,
    hp: 1,
    stress: 1,
    atk: { modifier: `–2`, name: `Daggers`, range: `Melee`, damage: `2 phy` },
    experience: `Thief +2`,
    features: [
      { name: `Minion (3)`, type: `Passive`, text: `The Lackey is defeated when they take any damage. For every 3 damage a PC deals to the Lackey, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Jagged Knife Lackeys within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 2 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Skeleton Warrior`,
    tier: 1,
    role: `Standard`,
    description: `A dirt-covered skeleton armed with a rusted blade.`,
    motives: `Feign death, gang up, steal skin`,
    difficulty: 10,
    thresholds: { major: `4`, severe: `8` },
    hp: 3,
    stress: 2,
    atk: { modifier: `+0`, name: `Sword`, range: `Melee`, damage: `1d6+2 phy` },
    features: [
      { name: `Only Bones`, type: `Passive`, text: `The Warrior is resistant to physical damage.` },
      { name: `Won't Stay Dead`, type: `Reaction`, text: `When the Warrior is defeated, you can spotlight them and roll a d6. On a result of 6, if there are other adversaries on the battlefield, the Warrior re-forms with no marked HP.` },
    ],
  },
  {
    name: `Glass Snake`,
    tier: 1,
    role: `Standard`,
    description: `A clear serpent with a massive head that leaves behind a glass shard trail wherever they go.`,
    motives: `Climb, feed, keep distance, scare`,
    difficulty: 14,
    thresholds: { major: `6`, severe: `10` },
    hp: 5,
    stress: 3,
    atk: { modifier: `+2`, name: `Glass Fangs`, range: `Very Close`, damage: `1d8+2 phy` },
    features: [
      { name: `Armor-Shredding Shards`, type: `Passive`, text: `After a successful attack against the Snake within Melee range, the attacker must mark an Armor Slot. If they can't mark an Armor Slot, they must mark an HP.` },
      { name: `Spinning Serpent`, type: `Action`, text: `Mark a Stress to make an attack against all targets within Very Close range. Targets the Snake succeeds against take 1d6+1 physical damage.` },
      { name: `Spitter`, type: `Action`, text: `Spend a Fear to introduce a d6 Spitter Die. When the Snake is in the spotlight, roll this die. On a result of 5 or higher, all targets in front of the Snake within Far range must succeed on an Agility Reaction Roll or take 1d4 physical damage. The Snake can take the spotlight a second time this GM turn.` },
    ],
  },
  {
    name: `Harrier`,
    tier: 1,
    role: `Standard`,
    description: `A nimble fighter armed with javelins.`,
    motives: `Flank, harry, kite, profit`,
    difficulty: 12,
    thresholds: { major: `5`, severe: `9` },
    hp: 3,
    stress: 3,
    atk: { modifier: `+1`, name: `Javelin`, range: `Close`, damage: `1d6+2 phy` },
    experience: `Camouflage +2`,
    features: [
      { name: `Maintain Distance`, type: `Passive`, text: `After making a standard attack, the Harrier can move anywhere within Far range.` },
      { name: `Fall Back`, type: `Reaction`, text: `When a creature moves into Melee range to make an attack, you can mark a Stress before the attack roll to move anywhere within Close range and make an attack against that creature. On a success, deal 1d10+2 physical damage.` },
    ],
  },
  {
    name: `Bladed Guard`,
    tier: 1,
    role: `Standard`,
    description: `An armored guard bearing a sword and shield painted in the settlement's colors.`,
    motives: `Arrest, close gates, make it through the day, pin down`,
    difficulty: 12,
    thresholds: { major: `5`, severe: `9` },
    hp: 5,
    stress: 2,
    atk: { modifier: `+1`, name: `Longsword`, range: `Melee`, damage: `1d6+1 phy` },
    experience: `Local Knowledge +3`,
    features: [
      { name: `Shield Wall`, type: `Passive`, text: `A creature who tries to move within Very Close range of the Guard must succeed on an Agility Roll. If additional Bladed Guards are standing in a line alongside the first, and each is within Melee range of another guard in the line, the Difficulty increases by the total number of guards in that line.` },
      { name: `Detain`, type: `Action`, text: `Make an attack against a target within Very Close range. On a success, mark a Stress to Restrain the target until they break free with a successful attack, Finesse Roll, or Strength Roll.` },
    ],
  },
  {
    name: `Jagged Knife Bandit`,
    tier: 1,
    role: `Standard`,
    description: `A cunning criminal in a cloak bearing one of the gang's iconic knives.`,
    motives: `Escape, profit, steal, throw smoke`,
    difficulty: 12,
    thresholds: { major: `8`, severe: `14` },
    hp: 5,
    stress: 3,
    atk: { modifier: `+1`, name: `Daggers`, range: `Melee`, damage: `1d8+1 phy` },
    experience: `Thief +2`,
    features: [
      { name: `Climber`, type: `Passive`, text: `The Bandit climbs just as easily as they run.` },
      { name: `From Above`, type: `Passive`, text: `When the Bandit succeeds on a standard attack from above a target, they deal 1d10+1 physical damage instead of their standard damage.` },
    ],
  },
  {
    name: `Sylvan Soldier`,
    tier: 1,
    role: `Standard`,
    description: `A faerie warrior adorned in armor made of leaves and bark.`,
    motives: `Ambush, hide, overwhelm, protect, trail`,
    difficulty: 11,
    thresholds: { major: `6`, severe: `11` },
    hp: 4,
    stress: 2,
    atk: { modifier: `+0`, name: `Scythe`, range: `Melee`, damage: `1d8+1 phy` },
    experience: `Tracker +2`,
    features: [
      { name: `Pack Tactics`, type: `Passive`, text: `If the Soldier makes a standard attack and another Sylvan Soldier is within Melee range of the target, deal 1d8+5 physical damage instead of their standard damage.` },
      { name: `Forest Control`, type: `Action`, text: `Spend a Fear to pull down a tree within Close range. A creature hit by the tree must succeed on an Agility Reaction Roll (15) or take 1d10 physical damage.` },
      { name: `Blend In`, type: `Reaction`, text: `When the Soldier makes a successful attack, you can mark a Stress to become Hidden until the Soldier's next attack or a PC succeeds on an Instinct Roll (14) to find them.` },
    ],
  },
  {
    name: `Archer Guard`,
    tier: 1,
    role: `Ranged`,
    description: `A tall guard bearing a longbow and quiver with arrows fletched in the settlement's colors.`,
    motives: `Arrest, close gates, make it through the day, pin down`,
    difficulty: 10,
    thresholds: { major: `4`, severe: `8` },
    hp: 3,
    stress: 2,
    atk: { modifier: `+1`, name: `Longbow`, range: `Far`, damage: `1d8+3 phy` },
    experience: `Local Knowledge +3`,
    features: [
      { name: `Hobbling Shot`, type: `Action`, text: `Make an attack against a target within Far range. On a success, mark a Stress to deal 1d12+3 physical damage. If the target marks HP from this attack, they have disadvantage on Agility Rolls until they clear at least 1 HP.` },
    ],
  },
  {
    name: `Skeleton Archer`,
    tier: 1,
    role: `Ranged`,
    description: `A fragile skeleton with a shortbow and arrows.`,
    motives: `Perforate distracted targets, play dead, steal skin`,
    difficulty: 9,
    thresholds: { major: `4`, severe: `7` },
    hp: 3,
    stress: 2,
    atk: { modifier: `+2`, name: `Shortbow`, range: `Far`, damage: `1d8+1 phy` },
    features: [
      { name: `Opportunist`, type: `Passive`, text: `When two or more adversaries are within Very Close range of a creature, all damage the Archer deals to that creature is doubled.` },
      { name: `Deadly Shot`, type: `Action`, text: `Make an attack against a Vulnerable target within Far range. On a success, mark a Stress to deal 3d4+8 physical damage.` },
    ],
  },
  {
    name: `Jagged Knife Sniper`,
    tier: 1,
    role: `Ranged`,
    description: `A lanky bandit striking from cover with a shortbow.`,
    motives: `Ambush, hide, profit, reposition`,
    difficulty: 13,
    thresholds: { major: `4`, severe: `7` },
    hp: 3,
    stress: 2,
    atk: { modifier: `–1`, name: `Shortbow`, range: `Far`, damage: `1d10+2 phy` },
    experience: `Stealth +2`,
    features: [
      { name: `Unseen Strike`, type: `Passive`, text: `If the Sniper is Hidden when they make a successful standard attack against a target, they deal 1d10+4 physical damage instead of their standard damage.` },
    ],
  },
  {
    name: `Dire Wolf`,
    tier: 1,
    role: `Skulk`,
    description: `A large wolf with menacing teeth, seldom encountered alone.`,
    motives: `Defend territory, harry, protect pack, surround, trail`,
    difficulty: 12,
    thresholds: { major: `5`, severe: `9` },
    hp: 4,
    stress: 3,
    atk: { modifier: `+2`, name: `Claws`, range: `Melee`, damage: `1d6+2 phy` },
    experience: `Keen Senses +3`,
    features: [
      { name: `Pack Tactics`, type: `Passive`, text: `If the Wolf makes a successful standard attack and another Dire Wolf is within Melee range of the target, deal 1d6+5 physical damage instead of their standard damage and you gain a Fear.` },
      { name: `Hobbling Strike`, type: `Action`, text: `Mark a Stress to make an attack against a target within Melee range. On a success, deal 3d4+10 direct physical damage and make them Vulnerable until they clear at least 1 HP.` },
    ],
  },
  {
    name: `Jagged Knife Shadow`,
    tier: 1,
    role: `Skulk`,
    description: `A nimble scoundrel bearing a wicked knife and utilizing shadow magic to isolate targets.`,
    motives: `Ambush, conceal, divide, profit`,
    difficulty: 12,
    thresholds: { major: `4`, severe: `8` },
    hp: 3,
    stress: 3,
    atk: { modifier: `+1`, name: `Daggers`, range: `Melee`, damage: `1d4+4 phy` },
    experience: `Intrusion +3`,
    features: [
      { name: `Backstab`, type: `Passive`, text: `When the Shadow succeeds on a standard attack that has advantage, they deal 1d6+6 physical damage instead of their standard damage.` },
      { name: `Cloaked`, type: `Action`, text: `Become Hidden until after the Shadow's next attack. Attacks made while Hidden from this feature have advantage.` },
    ],
  },
  {
    name: `Jagged Knife Hexer`,
    tier: 1,
    role: `Support`,
    description: `A staff-wielding bandit in a cloak adorned with magical paraphernalia, using curses to vex their foes.`,
    motives: `Command, hex, profit`,
    difficulty: 13,
    thresholds: { major: `5`, severe: `9` },
    hp: 4,
    stress: 4,
    atk: { modifier: `+2`, name: `Staff`, range: `Far`, damage: `1d6+2 mag` },
    experience: `Magical Knowledge +2`,
    features: [
      { name: `Curse`, type: `Action`, text: `Choose a target within Far range and temporarily Curse them. While the target is Cursed, you can mark a Stress when that target rolls with Hope to make the roll be with Fear instead.` },
      { name: `Chaotic Flux`, type: `Action`, text: `Make an attack against up to three targets within Very Close range. Mark a Stress to deal 2d6+3 magic damage to targets the Hexer succeeded against.` },
    ],
  },
  {
    name: `Courtier`,
    tier: 1,
    role: `Social`,
    description: `An ambitious and ostentatiously dressed socialite.`,
    motives: `Discredit, gain favor, maneuver, scheme`,
    difficulty: 12,
    thresholds: { major: `4`, severe: `8` },
    hp: 3,
    stress: 4,
    atk: { modifier: `–4`, name: `Daggers`, range: `Melee`, damage: `1d4+2 phy` },
    experience: `Socialite +3`,
    features: [
      { name: `Mockery`, type: `Action`, text: `Mark a Stress to say something mocking and force a target within Close range to make a Presence Reaction Roll (14) to see if they can save face. On a failure, the target must mark 2 Stress and is Vulnerable until the scene ends.` },
      { name: `Scapegoat`, type: `Action`, text: `Spend a Fear and target a PC. The Courtier convinces a crowd or prominent individual that the target is the cause of their current conflict or misfortune.` },
    ],
  },
  {
    name: `Merchant`,
    tier: 1,
    role: `Social`,
    description: `A finely dressed trader with a keen eye for financial gain.`,
    motives: `Buy low and sell high, create demand, inflate prices, seek profit`,
    difficulty: 12,
    thresholds: { major: `4`, severe: `8` },
    hp: 3,
    stress: 3,
    atk: { modifier: `–4`, name: `Club`, range: `Melee`, damage: `1d4+1 phy` },
    experience: `Shrewd Negotiator +3`,
    features: [
      { name: `Preferential Treatment`, type: `Passive`, text: `A PC who succeeds on a Presence Roll against the Merchant gains a discount on purchases. A PC who fails must pay more and has disadvantage on future Presence Rolls against the Merchant.` },
      { name: `The Runaround`, type: `Passive`, text: `When a PC rolls a 14 or lower on a Presence Roll made against the Merchant, they must mark a Stress.` },
    ],
  },
  {
    name: `Giant Scorpion`,
    tier: 1,
    role: `Bruiser`,
    description: `A human-sized arachnid with tearing claws and a stinging tail.`,
    motives: `Ambush, feed, grapple, poison`,
    difficulty: 13,
    thresholds: { major: `7`, severe: `13` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+1`, name: `Pincers`, range: `Melee`, damage: `1d12+2 phy` },
    experience: `Camouflage +2`,
    features: [
      { name: `Double Strike`, type: `Action`, text: `Mark a Stress to make a standard attack against two targets within Melee range.` },
      { name: `Venomous Stinger`, type: `Action`, text: `Make an attack against a target within Very Close range. On a success, spend a Fear to deal 1d4+4 physical damage and Poison them until their next rest or they succeed on a Knowledge Roll (16). While Poisoned, the target must roll a d6 before they make an action roll. On a result of 4 or lower, they must mark a Stress.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Scorpion makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Deeproot Defender`,
    tier: 1,
    role: `Bruiser`,
    description: `A burly vegetable-person with grasping vines.`,
    motives: `Ambush, grab, protect, pummel`,
    difficulty: 10,
    thresholds: { major: `8`, severe: `14` },
    hp: 7,
    stress: 3,
    atk: { modifier: `+2`, name: `Vines`, range: `Close`, damage: `1d8+3 phy` },
    experience: `Huge +3`,
    features: [
      { name: `Ground Slam`, type: `Action`, text: `Slam the ground, knocking all targets within Very Close range back to Far range. Each target knocked back this way must mark a Stress.` },
      { name: `Grab and Drag`, type: `Action`, text: `Make an attack against a target within Close range. On a success, spend a Fear to pull them into Melee range, deal 1d6+2 physical damage, and Restrain them until the Defender takes Severe damage.` },
    ],
  },
  {
    name: `Bear`,
    tier: 1,
    role: `Bruiser`,
    description: `A large bear with thick fur and powerful claws.`,
    motives: `Climb, defend territory, pummel, track`,
    difficulty: 14,
    thresholds: { major: `9`, severe: `17` },
    hp: 7,
    stress: 2,
    atk: { modifier: `+1`, name: `Claws`, range: `Melee`, damage: `1d8+3 phy` },
    experience: `Ambusher +3, Keen Senses +2`,
    features: [
      { name: `Overwhelming Force`, type: `Passive`, text: `Targets who mark HP from the Bear's standard attack are knocked back to Very Close range.` },
      { name: `Bite`, type: `Action`, text: `Mark a Stress to make an attack against a target within Melee range. On a success, deal 3d4+10 physical damage and the target is Restrained until they break free with a successful Strength Roll.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Bear makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Skeleton Knight`,
    tier: 1,
    role: `Bruiser`,
    description: `A large armored skeleton with a huge blade.`,
    motives: `Cut down the living, steal skin, wreak havoc`,
    difficulty: 13,
    thresholds: { major: `7`, severe: `13` },
    hp: 5,
    stress: 2,
    atk: { modifier: `+2`, name: `Rusty Greatsword`, range: `Melee`, damage: `1d10+2 phy` },
    features: [
      { name: `Terrifying`, type: `Passive`, text: `When the Knight makes a successful attack, all PCs within Close range lose a Hope and you gain a Fear.` },
      { name: `Cut to the Bone`, type: `Action`, text: `Mark a Stress to make an attack against all targets within Very Close range. Targets the Knight succeeds against take 1d8+2 physical damage and must mark a Stress.` },
      { name: `Dig Two Graves`, type: `Reaction`, text: `When the Knight is defeated, they make an attack against a target within Very Close range (prioritizing the creature who killed them). On a success, the target takes 1d4+8 physical damage and loses 1d4 Hope.` },
    ],
  },
  {
    name: `Brawny Zombie`,
    tier: 1,
    role: `Bruiser`,
    description: `A large corpse, decay-bloated and angry.`,
    motives: `Crush, destroy, hail debris, slam`,
    difficulty: 10,
    thresholds: { major: `8`, severe: `15` },
    hp: 7,
    stress: 4,
    atk: { modifier: `+2`, name: `Slam`, range: `Very Close`, damage: `1d12+3 phy` },
    experience: `Collateral Damage +2, Throw +4`,
    features: [
      { name: `Slow`, type: `Passive`, text: `When you spotlight the Zombie and they don't have a token on their stat block, they can't act yet. Place a token on their stat block and describe what they're preparing to do. When you spotlight the Zombie and they have a token, clear the token and they can act.` },
      { name: `Rend Asunder`, type: `Action`, text: `Make a standard attack with advantage against a target the Zombie has Restrained. On a success, the attack deals direct damage.` },
      { name: `Rip and Tear`, type: `Reaction`, text: `When the Zombie makes a successful standard attack, you can mark a Stress to temporarily Restrain the target and force them to mark 2 Stress.` },
    ],
  },
  {
    name: `Weaponmaster`,
    tier: 1,
    role: `Bruiser`,
    description: `A master-at-arms wielding a sword twice their size.`,
    motives: `Act first, aim for the weakest, intimidate`,
    difficulty: 14,
    thresholds: { major: `8`, severe: `15` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+2`, name: `Claymore`, range: `Very Close`, damage: `1d12+2 phy` },
    features: [
      { name: `Goading Strike`, type: `Action`, text: `Make a standard attack against a target. On a success, mark a Stress to Taunt the target until their next successful attack. The next time the Taunted target attacks, they have disadvantage against targets other than the Weaponmaster.` },
      { name: `Adrenaline Burst`, type: `Action`, text: `Once per scene, spend a Fear to clear 2 HP and 2 Stress.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Weaponmaster makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Jagged Knife Kneebreaker`,
    tier: 1,
    role: `Bruiser`,
    description: `An imposing brawler carrying a large club.`,
    motives: `Grapple, intimidate, profit, steal`,
    difficulty: 12,
    thresholds: { major: `7`, severe: `14` },
    hp: 7,
    stress: 4,
    atk: { modifier: `–3`, name: `Club`, range: `Melee`, damage: `1d4+6 phy` },
    experience: `Thief +2, Unveiled Threats +3`,
    features: [
      { name: `I've Got 'Em`, type: `Passive`, text: `Creatures Restrained by the Kneebreaker take double damage from attacks by other adversaries.` },
      { name: `Hold Them Down`, type: `Action`, text: `Make an attack against a target within Melee range. On a success, the target takes no damage but is Restrained and Vulnerable. The target can break free, clearing both conditions, with a successful Strength Roll or is freed automatically if the Kneebreaker takes Major or greater damage.` },
    ],
  },
  {
    name: `Head Guard`,
    tier: 1,
    role: `Leader`,
    description: `A seasoned guard with a mace, a whistle, and a bellowing voice.`,
    motives: `Arrest, close gates, pin down, seek glory`,
    difficulty: 15,
    thresholds: { major: `7`, severe: `13` },
    hp: 7,
    stress: 3,
    atk: { modifier: `+4`, name: `Mace`, range: `Melee`, damage: `1d10+4 phy` },
    experience: `Commander +2, Local Knowledge +2`,
    features: [
      { name: `Rally Guards`, type: `Action`, text: `Spend 2 Fear to spotlight the Head Guard and up to 2d4 allies within Far range.` },
      { name: `On My Signal`, type: `Reaction`, text: `Countdown (5). When the Head Guard is in the spotlight for the first time, activate the countdown. It ticks down when a PC makes an attack roll. When it triggers, all Archer Guards within Far range make a standard attack with advantage against the nearest target within their range. If any attacks succeed on the same target, combine their damage.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Head Guard makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Jagged Knife Lieutenant`,
    tier: 1,
    role: `Leader`,
    description: `A seasoned bandit in quality leathers with a strong voice and cunning eyes.`,
    motives: `Bully, command, profit, reinforce`,
    difficulty: 13,
    thresholds: { major: `7`, severe: `14` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+2`, name: `Javelin`, range: `Close`, damage: `1d8+3 phy` },
    experience: `Local Knowledge +2`,
    features: [
      { name: `Tactician`, type: `Action`, text: `When you spotlight the Lieutenant, mark a Stress to also spotlight two allies within Close range.` },
      { name: `More Where That Came From`, type: `Action`, text: `Summon three Jagged Knife Lackeys, who appear at Far range.` },
      { name: `Coup de Grace`, type: `Action`, text: `Spend a Fear to make an attack against a Vulnerable target within Close range. On a success, deal 2d6+12 physical damage and the target must mark a Stress.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Lieutenant makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Spellblade`,
    tier: 1,
    role: `Leader`,
    description: `A mercenary combining swordplay and magic to deadly effect.`,
    motives: `Blast, command, endure`,
    difficulty: 14,
    thresholds: { major: `8`, severe: `14` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+3`, name: `Empowered Longsword`, range: `Melee`, damage: `1d8+4 phy/mag` },
    experience: `Magical Knowledge +2`,
    features: [
      { name: `Arcane Steel`, type: `Passive`, text: `Damage dealt by the Spellblade's standard attack is considered both physical and magic.` },
      { name: `Suppressing Blast`, type: `Action`, text: `Mark a Stress and target a group within Far range. All targets must succeed on an Agility Reaction Roll or take 1d8+2 magic damage. You gain a Fear for each target who marked HP from this attack.` },
      { name: `Move as a Unit`, type: `Action`, text: `Spend 2 Fear to spotlight up to five allies within Far range.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Spellblade makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Young Dryad`,
    tier: 1,
    role: `Leader`,
    description: `An imperious tree-person leading their forest's defenses.`,
    motives: `Command, nurture, prune the unwelcome`,
    difficulty: 11,
    thresholds: { major: `6`, severe: `11` },
    hp: 6,
    stress: 2,
    atk: { modifier: `+0`, name: `Scythe`, range: `Melee`, damage: `1d8+5 phy` },
    experience: `Leadership +3`,
    features: [
      { name: `Voice of the Forest`, type: `Action`, text: `Mark a Stress to spotlight 1d4 allies within range of a target they can attack without moving. On a success, their attacks deal half damage.` },
      { name: `Thorny Cage`, type: `Action`, text: `Spend a Fear to form a cage around a target within Very Close range and Restrain them until they're freed with a successful Strength Roll. When a creature makes an action roll against the cage, they must mark a Stress.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Dryad makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Acid Burrower`,
    tier: 1,
    role: `Solo`,
    description: `A horse-sized insect with digging claws and acidic blood.`,
    motives: `Burrow, drag away, feed, reposition`,
    difficulty: 14,
    thresholds: { major: `8`, severe: `15` },
    hp: 8,
    stress: 3,
    atk: { modifier: `+3`, name: `Claws`, range: `Very Close`, damage: `1d12+2 phy` },
    experience: `Tremor Sense +2`,
    features: [
      { name: `Relentless (3)`, type: `Passive`, text: `The Burrower can be spotlighted up to three times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Earth Eruption`, type: `Action`, text: `Mark a Stress to have the Burrower burst out of the ground. All creatures within Very Close range must succeed on an Agility Reaction Roll or be knocked over, making them Vulnerable until they next act.` },
      { name: `Spit Acid`, type: `Action`, text: `Make an attack against all targets in front of the Burrower within Close range. Targets the Burrower succeeds against take 2d6 physical damage and must mark an Armor Slot without receiving its benefits. If they can't mark an Armor Slot, they must mark an additional HP and you gain a Fear.` },
      { name: `Acid Bath`, type: `Reaction`, text: `When the Burrower takes Severe damage, all creatures within Close range are bathed in their acidic blood, taking 1d10 physical damage. This splash covers the ground within Very Close range with blood, and all creatures other than the Burrower who move through it take 1d6 physical damage.` },
    ],
  },
  {
    name: `Construct`,
    tier: 1,
    role: `Solo`,
    description: `A roughly humanoid being of stone and steel, assembled and animated by magic.`,
    motives: `Destroy environment, serve creator, smash target, trample groups`,
    difficulty: 13,
    thresholds: { major: `7`, severe: `15` },
    hp: 9,
    stress: 4,
    atk: { modifier: `+4`, name: `Fist Slam`, range: `Melee`, damage: `1d20 phy` },
    features: [
      { name: `Relentless (2)`, type: `Passive`, text: `The Construct can be spotlighted up to two times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Weak Structure`, type: `Passive`, text: `When the Construct marks HP from physical damage, they must mark an additional HP.` },
      { name: `Trample`, type: `Action`, text: `Mark a Stress to make an attack against all targets in the Construct's path when they move. Targets the Construct succeeds against take 1d8 physical damage.` },
      { name: `Overload`, type: `Reaction`, text: `Before rolling damage for the Construct's attack, you can mark a Stress to gain a +10 bonus to the damage roll. The Construct can then take the spotlight again.` },
      { name: `Death Quake`, type: `Reaction`, text: `When the Construct marks their last HP, the magic powering them ruptures in an explosion of force. Make an attack with advantage against all targets within Very Close range. Targets the Construct succeeds against take 1d12+2 magic damage.` },
    ],
  },
  {
    name: `Minor Chaos Elemental`,
    tier: 1,
    role: `Solo`,
    description: `A coruscating mass of uncontrollable magic.`,
    motives: `Confound, destabilize, transmogrify`,
    difficulty: 14,
    thresholds: { major: `7`, severe: `14` },
    hp: 7,
    stress: 3,
    atk: { modifier: `+3`, name: `Warp Blast`, range: `Close`, damage: `1d12+6 mag` },
    features: [
      { name: `Arcane Form`, type: `Passive`, text: `The Elemental is resistant to magic damage.` },
      { name: `Sickening Flux`, type: `Action`, text: `Mark a HP to force all targets within Close range to mark a Stress and become Vulnerable until their next rest or they clear a HP.` },
      { name: `Remake Reality`, type: `Action`, text: `Spend a Fear to transform the area within Very Close range into a different biome. All targets within this area take 2d6+3 direct magic damage.` },
      { name: `Magical Reflection`, type: `Reaction`, text: `When the Elemental takes damage from an attack within Close range, deal an amount of damage to the attacker equal to half the damage they dealt.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Elemental makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Minor Fire Elemental`,
    tier: 1,
    role: `Solo`,
    description: `A living flame the size of a large bonfire.`,
    motives: `Encircle enemies, grow in size, intimidate, start fires`,
    difficulty: 13,
    thresholds: { major: `7`, severe: `15` },
    hp: 9,
    stress: 3,
    atk: { modifier: `+3`, name: `Elemental Blast`, range: `Far`, damage: `1d10+4 mag` },
    features: [
      { name: `Relentless (2)`, type: `Passive`, text: `The Elemental can be spotlighted up to two times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Scorched Earth`, type: `Action`, text: `Mark a Stress to choose a point within Far range. The ground within Very Close range of that point immediately bursts into flames. All creatures within this area must make an Agility Reaction Roll. Targets who fail take 2d8 magic damage. Targets who succeed take half damage.` },
      { name: `Explosion`, type: `Action`, text: `Spend a Fear to erupt in a fiery explosion. Make an attack against all targets within Close range. Targets the Elemental succeeds against take 1d8 magic damage and are knocked back to Far range.` },
      { name: `Consume Kindling`, type: `Reaction`, text: `Three times per scene, when the Elemental moves onto objects that are highly flammable, consume them to clear a HP or a Stress.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Elemental makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Minor Demon`,
    tier: 1,
    role: `Solo`,
    description: `A crimson-hued creature from the Circles Below, consumed by rage against all mortals.`,
    motives: `Act erratically, corral targets, relish pain, torment`,
    difficulty: 14,
    thresholds: { major: `8`, severe: `15` },
    hp: 8,
    stress: 4,
    atk: { modifier: `+3`, name: `Claws`, range: `Melee`, damage: `1d8+6 phy` },
    features: [
      { name: `Relentless (2)`, type: `Passive`, text: `The Demon can be spotlighted up to two times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `All Must Fall`, type: `Passive`, text: `When a PC rolls a failure with Fear while within Close range of the Demon, they lose a Hope.` },
      { name: `Hellfire`, type: `Action`, text: `Spend a Fear to rain down hellfire within Far range. All targets within the area must make an Agility Reaction Roll. Targets who fail take 1d20+3 magic damage. Targets who succeed take half damage.` },
      { name: `Reaper`, type: `Reaction`, text: `Before rolling damage for the Demon's attack, you can mark a Stress to gain a bonus to the damage roll equal to the Demon's current number of marked HP.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Demon makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Patchwork Zombie Hulk`,
    tier: 1,
    role: `Solo`,
    description: `A towering gestalt of corpses moving as one, with torso-sized limbs and fists as large as a grown halfling.`,
    motives: `Absorb corpses, flail, hunger, terrify`,
    difficulty: 13,
    thresholds: { major: `8`, severe: `15` },
    hp: 10,
    stress: 3,
    atk: { modifier: `+4`, name: `Too Many Arms`, range: `Very Close`, damage: `1d20 phy` },
    experience: `Intimidation +2, Tear Things Apart +2`,
    features: [
      { name: `Destructible`, type: `Passive`, text: `When the Zombie takes Major or greater damage, they mark an additional HP.` },
      { name: `Flailing Limbs`, type: `Passive`, text: `When the Zombie makes a standard attack, they can attack all targets within Very Close range.` },
      { name: `Another for the Pile`, type: `Action`, text: `When the Zombie is within Very Close range of a corpse, they can incorporate it into themselves, clearing a HP and a Stress.` },
      { name: `Tormented Screams`, type: `Action`, text: `Mark a Stress to cause all PCs within Far range to make a Presence Reaction Roll (13). Targets who fail lose a Hope and you gain a Fear for each. Targets who succeed must mark a Stress.` },
    ],
  },
  {
    name: `Giant Mosquitoes`,
    tier: 1,
    role: `Horde`,
    roleSubtype: `5/HP`,
    description: `Dozens of fist-sized mosquitoes, flying together for protection.`,
    motives: `Fly away, harass, steal blood`,
    difficulty: 10,
    thresholds: { major: `5`, severe: `9` },
    hp: 6,
    stress: 3,
    atk: { modifier: `–2`, name: `Proboscis`, range: `Melee`, damage: `1d8+3 phy` },
    experience: `Camouflage +2`,
    features: [
      { name: `Horde (1d4+1)`, type: `Passive`, text: `When the Mosquitoes have marked half or more of their HP, their standard attack deals 1d4+1 physical damage instead.` },
      { name: `Flying`, type: `Passive`, text: `While flying, the Mosquitoes have a +2 bonus to their Difficulty.` },
      { name: `Bloodsucker`, type: `Reaction`, text: `When the Mosquitoes' attack causes a target to mark HP, you can mark a Stress to force the target to mark an additional HP.` },
    ],
  },
  {
    name: `Swarm of Rats`,
    tier: 1,
    role: `Horde`,
    roleSubtype: `10/HP`,
    description: `A skittering mass of ordinary rodents moving as one like a ravenous wave.`,
    motives: `Consume, obscure, swarm`,
    difficulty: 10,
    thresholds: { major: `6`, severe: `10` },
    hp: 6,
    stress: 2,
    atk: { modifier: `–3`, name: `Claws`, range: `Melee`, damage: `1d8+2 phy` },
    features: [
      { name: `Horde (1d4+1)`, type: `Passive`, text: `When the Swarm has marked half or more of their HP, their standard attack deals 1d4+1 physical damage instead.` },
      { name: `In Your Face`, type: `Passive`, text: `All targets within Melee range have disadvantage on attacks against targets other than the Swarm.` },
    ],
  },
  {
    name: `Tangle Bramble Swarm`,
    tier: 1,
    role: `Horde`,
    roleSubtype: `3/HP`,
    description: `A cluster of animate, blood-drinking tumbleweeds, each the size of a large gourd.`,
    motives: `Digest, entangle, immobilize`,
    difficulty: 12,
    thresholds: { major: `6`, severe: `11` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+0`, name: `Thorns`, range: `Melee`, damage: `1d6+3 phy` },
    experience: `Camouflage +2`,
    features: [
      { name: `Horde (1d4+2)`, type: `Passive`, text: `When the Swarm has marked half or more of their HP, their standard attack deals 1d4+2 physical damage instead.` },
      { name: `Crush`, type: `Action`, text: `Mark a Stress to deal 2d6+8 direct physical damage to a target with 3 or more bramble tokens.` },
      { name: `Encumber`, type: `Reaction`, text: `When the Swarm succeeds on an attack, give the target a bramble token. If a target has any bramble tokens, they are Restrained. If a target has 3 or more bramble tokens, they are also Vulnerable. All bramble tokens can be removed by succeeding on a Finesse Roll (12 + the number of bramble tokens) or dealing Major or greater damage to the Swarm.` },
    ],
  },
  {
    name: `Chaos Skull`,
    tier: 2,
    role: `Ranged`,
    description: `A floating humanoid skull animated by scintillating magic.`,
    motives: `Cackle, consume magic, serve creator`,
    difficulty: 15,
    thresholds: { major: `8`, severe: `16` },
    hp: 5,
    stress: 4,
    atk: { modifier: `+2`, name: `Energy Blast`, range: `Close`, damage: `2d8+3 mag` },
    features: [
      { name: `Levitation`, type: `Passive`, text: `The Skull levitates several feet off the ground and can't be Restrained.` },
      { name: `Wards`, type: `Passive`, text: `The Skull is resistant to magic damage.` },
      { name: `Magic Burst`, type: `Action`, text: `Mark a Stress to make an attack against all targets within Close range. Targets the Skull succeeds against take 2d6+4 magic damage.` },
      { name: `Siphon Magic`, type: `Action`, text: `Spend a Fear to make an attack against a PC with a Spellcast trait within Very Close range. On a success, the target marks 1d4 Stress and the Skull clears that many Stress. Additionally, on a success, the Skull can immediately be spotlighted again.` },
    ],
  },
  {
    name: `Cult Adept`,
    tier: 2,
    role: `Support`,
    description: `An experienced mage wielding shadow and fear.`,
    motives: `Curry favor, hinder foes, uncover knowledge`,
    difficulty: 14,
    thresholds: { major: `9`, severe: `18` },
    hp: 4,
    stress: 6,
    atk: { modifier: `+2`, name: `Rune-Covered Rod`, range: `Far`, damage: `2d4+3 mag` },
    experience: `Fallen Lore +2, Rituals +2`,
    features: [
      { name: `Enervating Blast`, type: `Action`, text: `Spend a Fear to make a standard attack against a target within range. On a success, the target must mark a Stress.` },
      { name: `Shroud of the Fallen`, type: `Action`, text: `Mark a Stress to wrap an ally within Close range in a shroud of Protection until the Adept marks their last HP. While Protected, the target has resistance to all damage.` },
      { name: `Shadow Shackles`, type: `Action`, text: `Spend a Fear and choose a point within Far range. All targets within Close range of that point are Restrained in smoky chains until they break free with a successful Strength or Instinct Roll. A target Restrained by this feature must spend a Hope to make an action roll.` },
      { name: `Fear Is Fuel`, type: `Reaction`, text: `Twice per scene, when a PC rolls a failure with Fear, clear a Stress.` },
    ],
  },
  {
    name: `Masked Thief`,
    tier: 2,
    role: `Skulk`,
    description: `A cunning thief with acrobatic skill and a flair for the dramatic.`,
    motives: `Evade, hide, pilfer, profit`,
    difficulty: 14,
    thresholds: { major: `8`, severe: `17` },
    hp: 4,
    stress: 5,
    atk: { modifier: `+3`, name: `Backsword`, range: `Melee`, damage: `2d8+3 phy` },
    experience: `Acrobatics +3`,
    features: [
      { name: `Quick Hands`, type: `Action`, text: `Make an attack against a target within Melee range. On a success, deal 1d8+2 physical damage and the Thief steals one item or consumable from the target's inventory.` },
      { name: `Escape Plan`, type: `Action`, text: `Mark a Stress to reveal a snare trap set anywhere on the battlefield by the Thief. All targets within Very Close range of the trap must succeed on an Agility Reaction Roll (13) or be pulled off their feet and suspended upside down. A target is Restrained and Vulnerable until they break free, ending both conditions, with a successful Finesse or Strength Roll (13).` },
    ],
  },
  {
    name: `Master Assassin`,
    tier: 2,
    role: `Leader`,
    description: `A seasoned killer with a threatening voice and a deadly blade.`,
    motives: `Ambush, get out alive, kill, prepare for all scenarios`,
    difficulty: 15,
    thresholds: { major: `12`, severe: `25` },
    hp: 7,
    stress: 5,
    atk: { modifier: `+5`, name: `Serrated Dagger`, range: `Close`, damage: `2d10+2 phy` },
    experience: `Command +3, Intrusion +3`,
    features: [
      { name: `Won't See It Coming`, type: `Passive`, text: `The Assassin deals direct damage while they're Hidden.` },
      { name: `Strike as One`, type: `Action`, text: `Mark a Stress to spotlight a number of other Assassins equal to the Assassin's unmarked Stress.` },
      { name: `The Subtle Blade`, type: `Reaction`, text: `When the Assassin successfully makes a standard attack against a Vulnerable target, you can spend a Fear to deal Severe damage instead of their standard damage.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Assassin makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Merchant Baron`,
    tier: 2,
    role: `Social`,
    description: `An accomplished merchant with a large operation under their command.`,
    motives: `Abuse power, gather resources, mobilize minions`,
    difficulty: 15,
    thresholds: { major: `9`, severe: `19` },
    hp: 5,
    stress: 3,
    atk: { modifier: `-2`, name: `Rapier`, range: `Melee`, damage: `1d6+2 phy` },
    experience: `Nobility +2, Trade +2`,
    features: [
      { name: `Everyone Has a Price`, type: `Action`, text: `Spend a Fear to offer a target a dangerous bargain for something they want or need. If used on a PC, they must make a Presence Reaction Roll (17). On a failure, they must mark 2 Stress or take the deal.` },
      { name: `The Best Muscle Money Can Buy`, type: `Action`, text: `Once per scene, mark a Stress to summon 1d4+1 Tier 1 adversaries, who appear at Far range, to enforce the Baron's will.` },
    ],
  },
  {
    name: `Minotaur Wrecker`,
    tier: 2,
    role: `Bruiser`,
    description: `A massive bull-headed firbolg with a quick temper.`,
    motives: `Consume, gore, navigate, overpower, pursue`,
    difficulty: 16,
    thresholds: { major: `14`, severe: `27` },
    hp: 7,
    stress: 5,
    atk: { modifier: `+2`, name: `Battleaxe`, range: `Very Close`, damage: `2d8+5 phy` },
    experience: `Navigation +2`,
    features: [
      { name: `Ramp Up`, type: `Passive`, text: `You must spend a Fear to spotlight the Minotaur. While spotlighted, they can make their standard attack against all targets within range.` },
      { name: `Charging Bull`, type: `Action`, text: `Mark a Stress to charge through a group within Close range and make an attack against all targets in the Minotaur's path. Targets the Minotaur succeeds against take 2d6+8 physical damage and are knocked back to Very Far range. If a target is knocked into a solid object or another creature, they take an extra 1d6 damage (combine the damage).` },
      { name: `Gore`, type: `Action`, text: `Make an attack against a target within Very Close range, moving the Minotaur into Melee range of them. On a success, deal 2d8 direct physical damage.` },
    ],
  },
  {
    name: `Mortal Hunter`,
    tier: 2,
    role: `Leader`,
    description: `An undead figure wearing a heavy leather coat, with searching eyes and a casually cruel demeanor.`,
    motives: `Devour, hunt, track`,
    difficulty: 16,
    thresholds: { major: `15`, severe: `27` },
    hp: 6,
    stress: 4,
    atk: { modifier: `+5`, name: `Tear at Flesh`, range: `Very Close`, damage: `2d12+1 phy` },
    experience: `Bloodhound +3`,
    features: [
      { name: `Terrifying`, type: `Passive`, text: `When the Hunter makes a successful attack, all PCs within Far range lose a Hope and you gain a Fear.` },
      { name: `Deathlock`, type: `Action`, text: `Spend a Fear to curse a target within Very Close range with a necrotic Deathlock until the end of the scene. Attacks made by the Hunter against a Deathlocked target deal direct damage. The Hunter can only maintain one Deathlock at a time.` },
      { name: `Inevitable Death`, type: `Action`, text: `Mark a Stress to spotlight 1d4 allies. Attacks they make while spotlighted in this way deal half damage.` },
      { name: `Rampage`, type: `Reaction`, text: `Countdown (Loop 1d6). When the Hunter is in the spotlight for the first time, activate the countdown. When it triggers, move the Hunter in a straight line to a point within Far range and make an attack against all targets in their path. Targets the Hunter succeeds against take 2d8+2 physical damage.` },
    ],
  },
  {
    name: `Royal Advisor`,
    tier: 2,
    role: `Social`,
    description: `A high-ranking courtier with the ear of the local nobility.`,
    motives: `Curry favor, manufacture evidence, scheme`,
    difficulty: 14,
    thresholds: { major: `8`, severe: `15` },
    hp: 3,
    stress: 3,
    atk: { modifier: `-3`, name: `Wand`, range: `Far`, damage: `1d4+3 phy` },
    experience: `Administration +3, Courtier +3`,
    features: [
      { name: `Devastating Retort`, type: `Passive`, text: `A PC who rolls less than 17 on an action roll targeting the Advisor must mark a Stress.` },
      { name: `Bend Ears`, type: `Action`, text: `Mark a Stress to influence an NPC within Melee range with whispered words. That target's opinion on one matter shifts toward the Advisor's preference unless it is in direct opposition to the target's motives.` },
      { name: `Scapegoat`, type: `Action`, text: `Spend a Fear to convince a crowd or notable individual that one person or group is responsible for some problem facing the target. The target becomes hostile to the scapegoat until convinced of their innocence with a successful Presence Roll (17).` },
    ],
  },
  {
    name: `Secret-Keeper`,
    tier: 2,
    role: `Leader`,
    description: `A clandestine leader with a direct channel to the Fallen Gods.`,
    motives: `Amass great power, plot, take command`,
    difficulty: 16,
    thresholds: { major: `13`, severe: `26` },
    hp: 7,
    stress: 4,
    atk: { modifier: `+3`, name: `Sigil-laden Staff`, range: `Far`, damage: `2d12 mag` },
    experience: `Coercion +2, Fallen Lore +2`,
    features: [
      { name: `Seize Your Moment`, type: `Action`, text: `Spend 2 Fear to spotlight 1d4 allies. Attacks they make while spotlighted in this way deal half damage.` },
      { name: `Our Master's Will`, type: `Reaction`, text: `When you spotlight an ally within Far range, mark a Stress to gain a Fear.` },
      { name: `Summoning Ritual`, type: `Reaction`, text: `Countdown (6). When the Secret-Keeper is in the spotlight for the first time, activate the countdown. When they mark HP, tick down this countdown by the number of HP marked. When it triggers, summon a Minor Demon who appears at Close range.` },
      { name: `Fallen Hounds`, type: `Reaction`, text: `Once per scene, when the Secret-Keeper marks 2 or more HP, you can mark a Stress to summon a Demonic Hound Pack, which appears at Close range and is immediately spotlighted.` },
    ],
  },
  {
    name: `Shark`,
    tier: 2,
    role: `Bruiser`,
    description: `A large aquatic predator, always on the move.`,
    motives: `Find the blood, isolate prey, target the weak`,
    difficulty: 14,
    thresholds: { major: `14`, severe: `28` },
    hp: 7,
    stress: 3,
    atk: { modifier: `+2`, name: `Toothy Maw`, range: `Very Close`, damage: `2d12+1 phy` },
    experience: `Sense of Smell +3`,
    features: [
      { name: `Terrifying`, type: `Passive`, text: `When the Shark makes a successful attack, all PCs within Far range lose a Hope and you gain a Fear.` },
      { name: `Rending Bite`, type: `Passive`, text: `When the Shark makes a successful attack, the target must mark an Armor Slot without receiving its benefits (they can still use armor to reduce the damage). If they can't mark an Armor Slot, they must mark an additional HP.` },
      { name: `Blood in the Water`, type: `Reaction`, text: `When a creature within Close range of the Shark marks HP from another creature's attack, you can mark a Stress to immediately spotlight the Shark, moving them into Melee range of the target and making a standard attack.` },
    ],
  },
  {
    name: `Siren`,
    tier: 2,
    role: `Skulk`,
    description: `A half-fish person with shimmering scales and an irresistible voice.`,
    motives: `Consume, lure prey, subdue with song`,
    difficulty: 14,
    thresholds: { major: `9`, severe: `18` },
    hp: 5,
    stress: 3,
    atk: { modifier: `+2`, name: `Distended Jaw Bite`, range: `Melee`, damage: `2d6+3 phy` },
    experience: `Song Repertoire +3`,
    features: [
      { name: `Captive Audience`, type: `Passive`, text: `If the Siren makes a standard attack against a target Entranced by their song, the attack deals 2d10+1 damage instead of their standard damage.` },
      { name: `Enchanting Song`, type: `Action`, text: `Spend a Fear to sing a song that affects all targets within Close range. Targets must succeed on an Instinct Reaction Roll or become Entranced until they mark 2 Stress. Other Sirens within Close range of the target can mark a Stress to each add a +1 bonus to the Difficulty of the reaction roll. While Entranced, a target can't act and is Vulnerable.` },
    ],
  },
  {
    name: `Spectral Archer`,
    tier: 2,
    role: `Ranged`,
    description: `A ghostly fighter with an ethereal bow, unable to move on while their charge is vulnerable.`,
    motives: `Move through solid objects, stay out of the fray, rehash old battles`,
    difficulty: 13,
    thresholds: { major: `6`, severe: `14` },
    hp: 3,
    stress: 3,
    atk: { modifier: `+3`, name: `Longbow`, range: `Far`, damage: `2d10+2 phy` },
    experience: `Ancient Knowledge +2`,
    features: [
      { name: `Ghost`, type: `Passive`, text: `The Archer has resistance to physical damage. Mark a Stress to move up to Close range through solid objects.` },
      { name: `Pick Your Target`, type: `Action`, text: `Spend a Fear to make an attack within Far range against a PC who is within Very Close range of at least two other PCs. On a success, the target takes 2d8+12 physical damage.` },
    ],
  },
  {
    name: `Spectral Captain`,
    tier: 2,
    role: `Leader`,
    description: `A ghostly commander leading their troops beyond death.`,
    motives: `Move through solid objects, rally troops, rehash old battles`,
    difficulty: 16,
    thresholds: { major: `13`, severe: `26` },
    hp: 6,
    stress: 4,
    atk: { modifier: `+3`, name: `Longbow`, range: `Far`, damage: `2d10+3 phy` },
    experience: `Ancient Knowledge +3`,
    features: [
      { name: `Ghost`, type: `Passive`, text: `The Captain has resistance to physical damage. Mark a Stress to move up to Close range through solid objects.` },
      { name: `Unending Battle`, type: `Action`, text: `Spend 2 Fear to return up to 1d4+1 defeated Spectral allies to the battle at the points where they first appeared (with no marked HP or Stress).` },
      { name: `Hold Fast`, type: `Reaction`, text: `When the Captain's Spectral allies are forced to make a reaction roll, you can mark a Stress to give those allies a +2 bonus to the roll.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Captain makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Spectral Guardian`,
    tier: 2,
    role: `Standard`,
    description: `A ghostly fighter with spears and swords, anchored by duty.`,
    motives: `Move through solid objects, protect treasure, rehash old battles`,
    difficulty: 15,
    thresholds: { major: `7`, severe: `15` },
    hp: 4,
    stress: 3,
    atk: { modifier: `+1`, name: `Spear`, range: `Very Close`, damage: `2d8+1 phy` },
    experience: `Ancient Knowledge +2`,
    features: [
      { name: `Ghost`, type: `Passive`, text: `The Guardian has resistance to physical damage. Mark a Stress to move up to Close range through solid objects.` },
      { name: `Grave Blade`, type: `Action`, text: `Spend a Fear to make an attack against a target within Very Close range. On a success, deal 2d10+6 physical damage and the target must mark a Stress.` },
    ],
  },
  {
    name: `Spy`,
    tier: 2,
    role: `Social`,
    description: `A skilled espionage agent with a knack for being in the right place to overhear secrets.`,
    motives: `Cut and run, disguise appearance, eavesdrop`,
    difficulty: 15,
    thresholds: { major: `8`, severe: `17` },
    hp: 4,
    stress: 3,
    atk: { modifier: `-2`, name: `Dagger`, range: `Melee`, damage: `2d6+3 phy` },
    experience: `Espionage +3`,
    features: [
      { name: `Gathering Secrets`, type: `Action`, text: `Spend a Fear to describe how the Spy knows a secret about a PC in the scene.` },
      { name: `Fly on the Wall`, type: `Reaction`, text: `When a PC or group is discussing something sensitive, you can mark a Stress to reveal that the Spy is present in the scene, observing them. If the Spy escapes the scene to report their findings, you gain 1d4 Fear.` },
    ],
  },
  {
    name: `Stonewraith`,
    tier: 2,
    role: `Skulk`,
    description: `A prowling hunter, like a slinking mountain lion, with a slate-gray stone body.`,
    motives: `Defend territory, isolate prey, stalk`,
    difficulty: 13,
    thresholds: { major: `11`, severe: `22` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+3`, name: `Bite and Claws`, range: `Melee`, damage: `2d8+6 phy` },
    experience: `Stonesense +3`,
    features: [
      { name: `Stonestrider`, type: `Passive`, text: `The Stonewraith can move through stone and earth as easily as air. While within stone or earth, they are Hidden and immune to all damage.` },
      { name: `Rocky Ambush`, type: `Action`, text: `While Hidden, mark a Stress to leap into Melee range with a target within Very Close range. The target must succeed on an Agility or Instinct Reaction Roll (15) or take 2d8 physical damage and become temporarily Restrained.` },
      { name: `Avalanche Roar`, type: `Action`, text: `Spend a Fear to roar while within a cave and cause a cave-in. All targets within Close range must succeed on an Agility Reaction Roll (14) or take 2d10 physical damage. The rubble can be cleared with a Progress Countdown (8).` },
      { name: `Momentum`, type: `Reaction`, text: `When the Stonewraith makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `War Wizard`,
    tier: 2,
    role: `Ranged`,
    description: `A battle-hardened mage trained in destructive magic.`,
    motives: `Develop new spells, seek power, shatter formations`,
    difficulty: 16,
    thresholds: { major: `11`, severe: `23` },
    hp: 5,
    stress: 6,
    atk: { modifier: `+4`, name: `Staff`, range: `Far`, damage: `2d10+4 mag` },
    experience: `Magical Knowledge +2, Strategize +2`,
    features: [
      { name: `Battle Teleport`, type: `Passive`, text: `Before or after making a standard attack, you can mark a Stress to teleport to a location within Far range.` },
      { name: `Refresh Warding Sphere`, type: `Action`, text: `Mark a Stress to refresh the Wizard's Warding Sphere reaction.` },
      { name: `Eruption`, type: `Action`, text: `Spend a Fear and choose a point within Far range. A Very Close area around that point erupts into impassable terrain. All targets within that area must make an Agility Reaction Roll (14). Targets who fail take 2d10 physical damage and are thrown out of the area. Targets who succeed take half damage and aren't moved.` },
      { name: `Arcane Artillery`, type: `Action`, text: `Spend a Fear to unleash a precise hail of magical blasts. All targets in the scene must make an Agility Reaction Roll. Targets who fail take 2d12 magic damage. Targets who succeed take half damage.` },
      { name: `Warding Sphere`, type: `Reaction`, text: `When the Wizard takes damage from an attack within Close range, deal 2d6 magic damage to the attacker. This reaction can't be used again until the Wizard refreshes it with their Refresh Warding Sphere action.` },
    ],
  },
  {
    name: `Adult Flickerfly`,
    tier: 3,
    role: `Solo`,
    description: `A winged insect the size of a large house with iridescent scales and wings that move too fast to track.`,
    motives: `Collect shiny things, hunt, nest, swoop`,
    difficulty: 17,
    thresholds: { major: `20`, severe: `35` },
    hp: 12,
    stress: 6,
    atk: { modifier: `+3`, name: `Wing Slash`, range: `Very Close`, damage: `3d20 phy` },
    features: [
      { name: `Relentless (4)`, type: `Passive`, text: `The Flickerfly can be spotlighted up to four times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Never Misses`, type: `Passive`, text: `When the Flickerfly makes an attack, the target's Evasion is halved against the attack.` },
      { name: `Deadly Flight`, type: `Passive`, text: `While flying, the Flickerfly can move up to Far range instead of Close range before taking an action.` },
      { name: `Whirlwind`, type: `Action`, text: `Spend a Fear to whirl, making an attack against all targets within Very Close range. Targets the Flickerfly succeeds against take 3d8 direct physical damage.` },
      { name: `Mind Dance`, type: `Action`, text: `Mark a Stress to create a magically dazzling display that grapples the minds of nearby foes. All targets within Close range must make an Instinct Reaction Roll. For each target who failed, you gain a Fear and the Flickerfly learns one of the target's fears.` },
      { name: `Hallucinatory Breath`, type: `Reaction`, text: `Countdown (Loop 1d6). When the Flickerfly takes damage for the first time, activate the countdown. When it triggers, the Flickerfly breathes hallucinatory gas on all targets in front of them up to Far range. Targets must make an Instinct Reaction Roll or be tormented by fearful hallucinations. Targets whose fears are known to the Flickerfly have disadvantage on this roll. Targets who fail lose 2 Hope and take 3d8+3 direct magic damage.` },
      { name: `Uncanny Reflexes`, type: `Reaction`, text: `When the Flickerfly takes damage from an attack within Close range, you can mark a Stress to take half damage.` },
    ],
  },
  {
    name: `Demon of Avarice`,
    tier: 3,
    role: `Support`,
    description: `A regal cloaked monstrosity with circular horns adorned with treasure.`,
    motives: `Consume, fuel greed, sow dissent`,
    difficulty: 17,
    thresholds: { major: `15`, severe: `29` },
    hp: 6,
    stress: 5,
    atk: { modifier: `+2`, name: `Hungry Maw`, range: `Melee`, damage: `3d6+5 mag` },
    experience: `Manipulation +3`,
    features: [
      { name: `Money Talks`, type: `Passive`, text: `Attacks against the Demon are made with disadvantage unless the attacker spends a handful of gold. This Demon starts with a number of handfuls equal to the number of PCs. When a target marks HP from the Demon's standard attack, they can spend a handful of gold instead of marking HP (1 handful per HP). Add a handful of gold to the Demon for each handful of gold spent by PCs on this feature.` },
      { name: `Numbers Must Go Up`, type: `Passive`, text: `Add a bonus to the Demon's attack rolls equal to the number of handfuls of gold they have.` },
      { name: `Money Is Time`, type: `Action`, text: `Spend 3 handfuls of gold (or a Fear) to spotlight 1d4+1 allies.` },
    ],
  },
  {
    name: `Demon of Despair`,
    tier: 3,
    role: `Skulk`,
    description: `A cloaked demon-creature with long limbs, seeping shadows.`,
    motives: `Make fear contagious, stick to the shadows, undermine resolve`,
    difficulty: 17,
    thresholds: { major: `18`, severe: `35` },
    hp: 6,
    stress: 5,
    atk: { modifier: `+3`, name: `Miasma Bolt`, range: `Far`, damage: `3d6+1 mag` },
    experience: `Manipulation +3`,
    features: [
      { name: `Depths of Despair`, type: `Passive`, text: `The Demon deals double damage to PCs with 0 Hope.` },
      { name: `Your Struggle Is Pointless`, type: `Action`, text: `Spend a Fear to weigh down the spirits of all PCs within Far range. All targets affected replace their Hope Die with a d8 until they roll a success with Hope or their next rest.` },
      { name: `Your Friends Will Fail You`, type: `Reaction`, text: `When a PC fails with Fear, you can mark a Stress to cause all other PCs within Close range to lose a Hope.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Demon makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Demon of Hubris`,
    tier: 3,
    role: `Leader`,
    description: `A perfectly beautiful and infinitely cruel demon with a gleaming spear and elegant robes.`,
    motives: `Condescend, declare premature victory, prove superiority`,
    difficulty: 18,
    thresholds: { major: `18`, severe: `36` },
    hp: 7,
    stress: 5,
    atk: { modifier: `+4`, name: `Perfect Spear`, range: `Very Close`, damage: `3d10 phy` },
    experience: `Manipulation +2`,
    features: [
      { name: `Terrifying`, type: `Passive`, text: `When the Demon makes a successful attack, all PCs within Far range must lose a Hope and you gain a Fear.` },
      { name: `Double or Nothing`, type: `Passive`, text: `When a PC within Far range fails a roll, they can choose to reroll their Fear Die and take the new result. If they still fail, they mark 2 Stress and the Demon clears a Stress.` },
      { name: `Unparalleled Skill`, type: `Action`, text: `Mark a Stress to deal the Demon's standard attack damage to a target within Close range.` },
      { name: `The Root of Villainy`, type: `Action`, text: `Spend a Fear to spotlight two other Demons within Far range.` },
      { name: `You Pale in Comparison`, type: `Reaction`, text: `When a PC fails a roll within Close range of the Demon, they must mark a Stress.` },
    ],
  },
  {
    name: `Demon of Jealousy`,
    tier: 3,
    role: `Ranged`,
    description: `A fickle creature of spindly limbs and insatiable desires.`,
    motives: `Join in on others' success, take what belongs to others, hold grudges`,
    difficulty: 17,
    thresholds: { major: `17`, severe: `30` },
    hp: 6,
    stress: 6,
    atk: { modifier: `+4`, name: `Psychic Assault`, range: `Far`, damage: `3d8+3 mag` },
    experience: `Manipulation +3`,
    features: [
      { name: `Unprotected Mind`, type: `Passive`, text: `The Demon's standard attack deals direct damage.` },
      { name: `My Turn`, type: `Reaction`, text: `When the Demon marks HP from an attack, spend a number of Fear equal to the HP marked by the Demon to cause the attacker to mark the same number of HP.` },
      { name: `Rivalry`, type: `Reaction`, text: `When a creature within Close range takes damage from a different adversary, you can mark a Stress to add a d4 to the damage roll.` },
      { name: `What's Yours Is Mine`, type: `Reaction`, text: `When a PC takes Severe damage within Very Close range of the Demon, you can spend a Fear to cause the target to make a Finesse Reaction Roll. On a failure, the Demon seizes one item or consumable of their choice from the target's inventory.` },
    ],
  },
  {
    name: `Demon of Wrath`,
    tier: 3,
    role: `Bruiser`,
    description: `A hulking demon with boulder-sized fists, driven by endless rage.`,
    motives: `Fuel anger, impress rivals, wreak havoc`,
    difficulty: 17,
    thresholds: { major: `22`, severe: `40` },
    hp: 7,
    stress: 5,
    atk: { modifier: `+3`, name: `Fists`, range: `Very Close`, damage: `3d8+1 mag` },
    experience: `Intimidation +2`,
    features: [
      { name: `Anger Unrelenting`, type: `Passive`, text: `The Demon's attacks deal direct damage.` },
      { name: `Battle Lust`, type: `Action`, text: `Spend a Fear to boil the blood of all PCs within Far range. They use a d20 as their Fear Die until the end of the scene.` },
      { name: `Retaliation`, type: `Reaction`, text: `When the Demon takes damage from an attack within Close range, you can mark a Stress to make a standard attack against the attacker.` },
      { name: `Blood and Souls`, type: `Reaction`, text: `Countdown (Loop 6). Activate the first time an attack is made within sight of the Demon. It ticks down when a PC takes a violent action. When it triggers, summon 1d4 Minor Demons, who appear at Close range.` },
    ],
  },
  {
    name: `Dire Bat`,
    tier: 3,
    role: `Skulk`,
    description: `A wide-winged pet endlessly loyal to their vampire owner.`,
    motives: `Dive-bomb, hide, protect leader`,
    difficulty: 14,
    thresholds: { major: `16`, severe: `30` },
    hp: 5,
    stress: 3,
    atk: { modifier: `+2`, name: `Claws and Teeth`, range: `Melee`, damage: `2d6+7 phy` },
    experience: `Bloodthirsty +3`,
    features: [
      { name: `Flying`, type: `Passive`, text: `While flying, the Bat gains a +3 bonus to their Difficulty.` },
      { name: `Screech`, type: `Action`, text: `Mark a Stress to send a high-pitch screech out toward all targets in front of the Bat within Far range. Those targets must mark 1d4 Stress.` },
      { name: `Guardian`, type: `Reaction`, text: `When an allied Vampire marks HP, you can mark a Stress to fly into Melee range of the attacker and make an attack with advantage against them. On a success, deal 2d6+2 physical damage.` },
    ],
  },
  {
    name: `Dryad`,
    tier: 3,
    role: `Leader`,
    description: `A nature spirit in the form of a humanoid tree.`,
    motives: `Command, cultivate, drive out, preserve the forest`,
    difficulty: 16,
    thresholds: { major: `24`, severe: `38` },
    hp: 8,
    stress: 5,
    atk: { modifier: `+4`, name: `Deadfall Shortbow`, range: `Far`, damage: `3d10+1 phy` },
    experience: `Forest Knowledge +4`,
    features: [
      { name: `Bramble Patch`, type: `Action`, text: `Mark a Stress to target a point within Far range. Create a patch of thorns that covers an area within Close range of that point. All targets within that area take 2d6+2 physical damage when they act. A target must succeed on a Finesse Roll or deal more than 20 damage to the Dryad with an attack to leave the area.` },
      { name: `Grow Saplings`, type: `Action`, text: `Spend a Fear to grow three Treant Sapling Minions, who appear at Close range and immediately take the spotlight.` },
      { name: `We Are All One`, type: `Reaction`, text: `When an ally dies within Close range, you can spend a Fear to clear 2 HP and 2 Stress as the fallen ally's life force is returned to the forest.` },
    ],
  },
  {
    name: `Elemental Spark`,
    tier: 3,
    role: `Minion`,
    description: `A blazing mote of elemental fire.`,
    motives: `Blast, consume, gain mass`,
    difficulty: 15,
    hp: 1,
    stress: 1,
    atk: { modifier: `+0`, name: `Bursts of Fire`, range: `Close`, damage: `5 mag` },
    features: [
      { name: `Minion (9)`, type: `Passive`, text: `The Elemental is defeated when they take any damage. For every 9 damage a PC deals to the Elemental, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Elemental Sparks within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 5 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Greater Earth Elemental`,
    tier: 3,
    role: `Bruiser`,
    description: `A living landslide of boulders and dust, as large as a house.`,
    motives: `Avalanche, knock over, pummel`,
    difficulty: 17,
    thresholds: { major: `22`, severe: `40` },
    hp: 10,
    stress: 4,
    atk: { modifier: `+7`, name: `Boulder Fist`, range: `Very Close`, damage: `3d10+1 phy` },
    features: [
      { name: `Slow`, type: `Passive`, text: `When you spotlight the Elemental and they don't have a token on their stat block, they can't act yet. Place a token on their stat block and describe what they're preparing to do. When you spotlight the Elemental and they have a token on their stat block, clear the token and they can act.` },
      { name: `Crushing Blows`, type: `Passive`, text: `When the Elemental makes a successful attack, the target must mark an Armor Slot without receiving its benefits (they can still use armor to reduce the damage). If they can't mark an Armor Slot, they must mark an additional HP.` },
      { name: `Immovable Object`, type: `Passive`, text: `An attack that would move the Elemental moves them two fewer ranges (for example, Far becomes Very Close). When the Elemental takes physical damage, reduce it by 7.` },
      { name: `Rockslide`, type: `Action`, text: `Mark a Stress to create a rockslide that buries the land in front of Elemental within Close range with rockfall. All targets in this area must make an Agility Reaction Roll (19). Targets who fail take 2d12+5 physical damage and become Vulnerable until their next roll with Hope. Targets who succeed take half damage.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Elemental makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Greater Water Elemental`,
    tier: 3,
    role: `Support`,
    description: `A huge living wave that crashes down upon enemies.`,
    motives: `Deluge, disperse, drown`,
    difficulty: 17,
    thresholds: { major: `17`, severe: `34` },
    hp: 5,
    stress: 5,
    atk: { modifier: `+3`, name: `Crashing Wave`, range: `Very Close`, damage: `3d4+1 mag` },
    features: [
      { name: `Water Jet`, type: `Action`, text: `Mark a Stress to attack a target within Very Close range. On a success, deal 2d4+7 physical damage and the target's next action has disadvantage. On a failure, the target must mark a Stress.` },
      { name: `Drowning Embrace`, type: `Action`, text: `Spend a Fear to make an attack against all targets within Very Close range. Targets the Elemental succeeds against become Restrained and Vulnerable as they begin drowning. A target can break free, ending both conditions, with a successful Strength or Instinct Roll.` },
      { name: `High Tide`, type: `Reaction`, text: `When the Elemental makes a successful standard attack, you can mark a Stress to knock the target back to Close range.` },
    ],
  },
  {
    name: `Head Vampire`,
    tier: 3,
    role: `Leader`,
    description: `A captivating undead dressed in aristocratic finery.`,
    motives: `Create thralls, charm, command, fly, intimidate`,
    difficulty: 17,
    thresholds: { major: `22`, severe: `42` },
    hp: 6,
    stress: 6,
    atk: { modifier: `+5`, name: `Rapier`, range: `Melee`, damage: `2d20+4 phy` },
    experience: `Aristocrat +3`,
    features: [
      { name: `Terrifying`, type: `Passive`, text: `When the Vampire makes a successful attack, all PCs within Far range lose a Hope and you gain a Fear.` },
      { name: `Look into My Eyes`, type: `Passive`, text: `A creature who moves into Melee range of the Vampire must make an Instinct Reaction Roll. On a failure, you gain 1d4 Fear.` },
      { name: `Feed on Followers`, type: `Action`, text: `When the Vampire is within Melee range of an ally, they can cause the ally to mark a HP. The Vampire then clears a HP.` },
      { name: `The Hunt Is On`, type: `Action`, text: `Spend 2 Fear to summon 1d4 Vampires, who appear at Far range and immediately take the spotlight.` },
      { name: `Lifesuck`, type: `Reaction`, text: `When the Vampire is spotlighted, roll a d8. On a result of 6 or higher, all targets within Very Close range must mark a HP.` },
    ],
  },
  {
    name: `Huge Green Ooze`,
    tier: 3,
    role: `Skulk`,
    description: `A translucent green mound of acid taller than most humans.`,
    motives: `Camouflage, creep up, envelop, multiply`,
    difficulty: 15,
    thresholds: { major: `15`, severe: `30` },
    hp: 7,
    stress: 4,
    atk: { modifier: `+3`, name: `Ooze Appendage`, range: `Melee`, damage: `3d8+1 mag` },
    experience: `Blend In +3`,
    features: [
      { name: `Slow`, type: `Passive`, text: `When you spotlight the Ooze and they don't have a token on their stat block, they can't act yet. Place a token on their stat block and describe what they're preparing to do. When you spotlight the Ooze and they have a token on their stat block, clear the token and they can act.` },
      { name: `Acidic Form`, type: `Passive`, text: `When the Ooze makes a successful attack, the target must mark an Armor Slot without receiving its benefits (they can still use armor to reduce the damage). If they can't mark an Armor Slot, they must mark an additional HP.` },
      { name: `Envelop`, type: `Action`, text: `Make an attack against a target within Melee range. On a success, the Ooze Envelops them and the target must mark 2 Stress. While Enveloped, the target must mark an additional Stress every time they make an action roll. When the Ooze takes Severe damage, all Enveloped targets are freed and the condition is cleared.` },
      { name: `Split`, type: `Reaction`, text: `When the Ooze has 4 or more HP marked, you can spend a Fear to split them into two Green Oozes (with no marked HP or Stress). Immediately spotlight both of them.` },
    ],
  },
  {
    name: `Hydra`,
    tier: 3,
    role: `Solo`,
    description: `A quadrupedal scaled beast with multiple long-necked heads, each filled with menacing fangs.`,
    motives: `Devour, regenerate, terrify`,
    difficulty: 18,
    thresholds: { major: `19`, severe: `35` },
    hp: 10,
    stress: 5,
    atk: { modifier: `+3`, name: `Bite`, range: `Close`, damage: `2d12+2 phy` },
    features: [
      { name: `Many-Headed Menace`, type: `Passive`, text: `The Hydra begins with three heads and can have up to five. When the Hydra takes Major or greater damage, they lose a head.` },
      { name: `Relentless (X)`, type: `Passive`, text: `The Hydra can be spotlighted X times per GM turn, where X is the Hydra's number of heads. Spend Fear as usual to spotlight them.` },
      { name: `Regeneration`, type: `Action`, text: `If the Hydra has any marked HP, spend a Fear to clear a HP and grow two heads.` },
      { name: `Terrifying Chorus`, type: `Action`, text: `All PCs within Far range lose 2 Hope.` },
      { name: `Magical Weakness`, type: `Reaction`, text: `When the Hydra takes magic damage, they become Dazed until the next roll with Fear. While Dazed, they can't use their Regeneration action but are immune to magic damage.` },
    ],
  },
  {
    name: `Monarch`,
    tier: 3,
    role: `Social`,
    description: `The sovereign ruler of a nation, wreathed in the privilege of tradition and wielding unmatched power in their domain.`,
    motives: `Control vassals, destroy rivals, forge a legacy`,
    difficulty: 16,
    thresholds: { major: `16`, severe: `32` },
    hp: 6,
    stress: 5,
    atk: { modifier: `+0`, name: `Warhammer`, range: `Melee`, damage: `3d6+3 phy` },
    experience: `History +3, Nobility +3`,
    features: [
      { name: `Execute Them!`, type: `Action`, text: `Spend a Fear per PC in the party to have the group condemned for crimes real or imagined. A PC who succeeds on a Presence Roll can demand trial by combat or another special form of trial.` },
      { name: `Crownsguard`, type: `Action`, text: `Once per scene, mark a Stress to summon six Tier 3 Minions, who appear at Close range to enforce the Monarch's will.` },
      { name: `Casus Belli`, type: `Reaction`, text: `Long-Term Countdown (8). Spend a Fear to activate after the Monarch's desire for war is first revealed. When it triggers, the Monarch has a reason to rally the nation to war and the support to act on that reason. You gain 1d4 Fear.` },
    ],
  },
  {
    name: `Oak Treant`,
    tier: 3,
    role: `Bruiser`,
    description: `A sturdy animate old-growth tree.`,
    motives: `Hide in plain sight, preserve the forest, root down, swing branches`,
    difficulty: 17,
    thresholds: { major: `22`, severe: `40` },
    hp: 7,
    stress: 4,
    atk: { modifier: `+2`, name: `Branch`, range: `Very Close`, damage: `3d8+2 phy` },
    experience: `Forest Knowledge +3`,
    features: [
      { name: `Just a Tree`, type: `Passive`, text: `Before they make their first attack in a fight or after they become Hidden, the Treant is indistinguishable from other trees until they next act or a PC succeeds on an Instinct Roll to identify them.` },
      { name: `Seed Barrage`, type: `Action`, text: `Mark a Stress and make an attack against up to three targets within Close range, pummeling them with giant acorns. Targets the Treant succeeds against take 2d10+5 physical damage.` },
      { name: `Take Root`, type: `Action`, text: `Mark a Stress to Root the Treant in place. The Treant is Restrained while Rooted, and can end this effect instead of moving while they are spotlighted. While Rooted, the Treant has resistance to physical damage.` },
    ],
  },
  {
    name: `Stag Knight`,
    tier: 3,
    role: `Standard`,
    description: `A knight with huge, majestic antlers wearing armor made of dangerous thorns.`,
    motives: `Isolate, maneuver, protect the forest, weed the unwelcome`,
    difficulty: 17,
    thresholds: { major: `19`, severe: `36` },
    hp: 7,
    stress: 5,
    atk: { modifier: `+3`, name: `Bramble Sword`, range: `Melee`, damage: `3d8+3 phy` },
    experience: `Forest Knowledge +3`,
    features: [
      { name: `From Above`, type: `Passive`, text: `When the Knight succeeds on a standard attack from above a target, they deal 3d12+3 physical damage instead of their standard damage.` },
      { name: `Blade of the Forest`, type: `Action`, text: `Spend a Fear to make an attack against all targets within Very Close range. Targets the Knight succeeds against take physical damage equal to 3d4 + the target's Major threshold.` },
      { name: `Thorny Armor`, type: `Reaction`, text: `When the Knight takes damage from an attack within Melee range, you can mark a Stress to deal 1d10+5 physical damage to the attacker.` },
    ],
  },
  {
    name: `Treant Sapling`,
    tier: 3,
    role: `Minion`,
    description: `A small, sentient tree sapling.`,
    motives: `Blend in, preserve the forest, pummel, surround`,
    difficulty: 14,
    hp: 1,
    stress: 1,
    atk: { modifier: `+0`, name: `Branches`, range: `Melee`, damage: `8 phy` },
    features: [
      { name: `Minion (6)`, type: `Passive`, text: `The Sapling is defeated when they take any damage. For every 6 damage a PC deals to the Sapling, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Treant Saplings within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 8 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Vampire`,
    tier: 3,
    role: `Standard`,
    description: `An intelligent undead with blood-stained lips and a predator's smile.`,
    motives: `Bite, charm, deceive, feed, intimidate`,
    difficulty: 16,
    thresholds: { major: `18`, severe: `35` },
    hp: 5,
    stress: 4,
    atk: { modifier: `+3`, name: `Rapier`, range: `Melee`, damage: `3d8 phy` },
    experience: `Nocturnal Hunter +3`,
    features: [
      { name: `Draining Bite`, type: `Action`, text: `Make an attack against a target within Melee range. On a success, deal 5d4 physical damage. A target who marks HP from this attack loses a Hope and must mark a Stress. The Vampire then clears a HP.` },
      { name: `Mistform`, type: `Reaction`, text: `When the Vampire takes physical damage, you can spend a Fear to take half damage.` },
    ],
  },
  {
    name: `Vault Guardian Gaoler`,
    tier: 3,
    role: `Support`,
    description: `A boxy, dust-covered construct with thick metallic swinging doors on their torso.`,
    motives: `Carry away, entrap, protect, pummel`,
    difficulty: 16,
    thresholds: { major: `19`, severe: `33` },
    hp: 5,
    stress: 3,
    atk: { modifier: `+2`, name: `Body Bash`, range: `Very Close`, damage: `3d6+2 phy` },
    features: [
      { name: `Blocking Shield`, type: `Passive`, text: `Creatures within Melee range of the Gaoler have disadvantage on attack rolls against them. Creatures trapped inside the Gaoler are immune to this feature.` },
      { name: `Lock Up`, type: `Action`, text: `Mark a Stress to make an attack against a target within Very Close range. On a success, the target is Restrained within the Gaoler until freed with a successful Strength Roll (18). While Restrained, the target can only attack the Gaoler.` },
    ],
  },
  {
    name: `Vault Guardian Sentinel`,
    tier: 3,
    role: `Bruiser`,
    description: `A dust-covered golden construct with boxy limbs and a huge mace for a hand.`,
    motives: `Destroy at any cost, expunge, protect`,
    difficulty: 17,
    thresholds: { major: `21`, severe: `40` },
    hp: 6,
    stress: 3,
    atk: { modifier: `+3`, name: `Charged Mace`, range: `Very Close`, damage: `2d12+1 phy` },
    features: [
      { name: `Kinetic Slam`, type: `Passive`, text: `Targets who take damage from the Sentinel's standard attack are knocked back to Very Close range.` },
      { name: `Box In`, type: `Action`, text: `Mark a Stress to choose a target within Very Close range to focus on. That target has disadvantage on attack rolls when they're within Very Close range of the Sentinel. The Sentinel can only focus on one target at a time.` },
      { name: `Mana Bolt`, type: `Action`, text: `Spend a Fear to lob explosive magic at a point within Far range. All targets within Very Close range of that point must make an Agility Reaction Roll. Targets who fail take 2d8+20 magic damage and are knocked back to Close range. Targets who succeed take half damage and aren't knocked back.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Sentinel makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Vault Guardian Turret`,
    tier: 3,
    role: `Ranged`,
    description: `A massive living turret with reinforced armor and twelve piston-driven mechanical legs.`,
    motives: `Concentrate fire, lock down, mark, protect`,
    difficulty: 16,
    thresholds: { major: `20`, severe: `32` },
    hp: 5,
    stress: 4,
    atk: { modifier: `+3`, name: `Magitech Cannon`, range: `Far`, damage: `3d10+3 mag` },
    features: [
      { name: `Slow Firing`, type: `Passive`, text: `When you spotlight the Turret and they don't have a token on their stat block, they can't make a standard attack. Place a token on their stat block and describe what they're preparing to do. When you spotlight the Turret and they have a token on their stat block, clear the token and they can attack.` },
      { name: `Mark Target`, type: `Action`, text: `Spend a Fear to Mark a target within Far range until the Turret is destroyed or the Marked target becomes Hidden. While the target is Marked, their Evasion is halved.` },
      { name: `Concentrate Fire`, type: `Reaction`, text: `When another adversary deals damage to a target within Far range of the Turret, you can mark a Stress to add the Turret's standard attack damage to the damage roll.` },
      { name: `Detonation`, type: `Reaction`, text: `When the Turret is destroyed, they explode. All targets within Close range must make an Agility Reaction Roll. Targets who fail take 3d20 physical damage. Targets who succeed take half damage.` },
    ],
  },
  {
    name: `Young Ice Dragon`,
    tier: 3,
    role: `Solo`,
    description: `A glacier-blue dragon with four powerful limbs and frost-tinged wings.`,
    motives: `Avalanche, defend lair, fly, freeze, defend what is mine, maul`,
    difficulty: 18,
    thresholds: { major: `21`, severe: `41` },
    hp: 10,
    stress: 6,
    atk: { modifier: `+7`, name: `Bite and Claws`, range: `Close`, damage: `4d10 phy` },
    experience: `Protect What Is Mine +3`,
    features: [
      { name: `Relentless (3)`, type: `Passive`, text: `The Dragon can be spotlighted up to three times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Rend and Crush`, type: `Passive`, text: `If a target damaged by the Dragon doesn't mark an Armor Slot to reduce the damage, they must mark a Stress.` },
      { name: `No Hope`, type: `Passive`, text: `When a PC rolls with Fear while within Far range of the Dragon, they lose a Hope.` },
      { name: `Blizzard Breath`, type: `Action`, text: `Spend 2 Fear to release an icy whorl in front of the Dragon within Close range. All targets in this area must make an Agility Reaction Roll. Targets who fail take 4d6+5 magic damage and are Restrained by ice until they break free with a successful Strength Roll. Targets who succeed must mark 2 Stress or take half damage.` },
      { name: `Avalanche`, type: `Action`, text: `Spend a Fear to have the Dragon unleash a huge downfall of snow and ice, covering all other creatures within Far range. All targets within this area must succeed on an Instinct Reaction Roll or be buried in snow and rocks, becoming Vulnerable until they dig themselves out from the debris. For each PC that fails the reaction roll, you gain a Fear.` },
      { name: `Frozen Scales`, type: `Reaction`, text: `When a creature makes a successful attack against the Dragon from within Very Close range, they must mark a Stress and become Chilled until their next rest or they clear a Stress. While they are Chilled, they have disadvantage on attack rolls.` },
      { name: `Momentum`, type: `Reaction`, text: `When the Dragon makes a successful attack against a PC, you gain a Fear.` },
    ],
  },
  {
    name: `Arch-Necromancer`,
    tier: 4,
    role: `Leader`,
    description: `A decaying mage adorned in dark, tattered robes.`,
    motives: `Corrupt, decay, flee to fight another day, resurrect`,
    difficulty: 21,
    thresholds: { major: `33`, severe: `66` },
    hp: 9,
    stress: 8,
    atk: { modifier: `+6`, name: `Necrotic Blast`, range: `Far`, damage: `4d12+8 mag` },
    experience: `Forbidden Knowledge +3, Wisdom of Centuries +3`,
    features: [
      { name: `Dance of Death`, type: `Action`, text: `Mark a Stress to spotlight 1d4 allies. Attacks they make while spotlighted in this way deal half damage, or full damage if you spend a Fear.` },
      { name: `Beam of Decay`, type: `Action`, text: `Mark 2 Stress to cause all targets within Far range to make a Strength Reaction Roll. Targets who fail take 2d20+12 magic damage and you gain a Fear. Targets who succeed take half damage. A target who marks 2 or more HP must also mark 2 Stress and becomes Vulnerable until they roll with Hope.` },
      { name: `Open the Gates of Death`, type: `Action`, text: `Spend a Fear to summon a Zombie Legion, which appears at Close range and immediately takes the spotlight.` },
      { name: `Not Today, My Dears`, type: `Reaction`, text: `When the Necromancer has marked 7 or more of their HP, you can spend a Fear to have them teleport away to a safe location to recover. A PC who succeeds on an Instinct Roll can trace the teleportation magic to their destination.` },
      { name: `Your Life Is Mine`, type: `Reaction`, text: `Countdown (Loop 2d6). When the Necromancer has marked 6 or more of their HP, activate the countdown. When it triggers, deal 2d10+6 direct magic damage to a target within Close range. The Necromancer then clears a number of Stress or HP equal to the number of HP marked by the target from this attack.` },
    ],
  },
  {
    name: `Fallen Shock Troop`,
    tier: 4,
    role: `Minion`,
    description: `A cursed soul bound to the Fallen's will.`,
    motives: `Crush, dominate, earn relief, punish`,
    difficulty: 18,
    hp: 1,
    stress: 1,
    atk: { modifier: `+2`, name: `Cursed Axe`, range: `Very Close`, damage: `12 phy` },
    features: [
      { name: `Minion (12)`, type: `Passive`, text: `The Shock Troop is defeated when they take any damage. For every 12 damage a PC deals to the Shock Troop, defeat an additional Minion within range the attack would succeed against.` },
      { name: `Aura of Doom`, type: `Passive`, text: `When a PC marks HP from an attack by the Shock Troop, they lose a Hope.` },
      { name: `Group Attack`, type: `Action`, text: `Spend a Fear to choose a target and spotlight all Fallen Shock Troops within Close range of them. Those Minions move into Melee range of the target and make one shared attack roll. On a success, they deal 12 physical damage each. Combine this damage.` },
    ],
  },
  {
    name: `Fallen Sorcerer`,
    tier: 4,
    role: `Support`,
    description: `A powerful mage bound by the bargains they made in life.`,
    motives: `Acquire, dishearten, dominate, torment`,
    difficulty: 19,
    thresholds: { major: `26`, severe: `42` },
    hp: 6,
    stress: 5,
    atk: { modifier: `+4`, name: `Corrupted Staff`, range: `Far`, damage: `4d6+10 mag` },
    experience: `Ancient Knowledge +2`,
    features: [
      { name: `Conflagration`, type: `Action`, text: `Spend a Fear to unleash an all-consuming firestorm and make an attack against all targets within Close range. Targets the Sorcerer succeeds against take 2d10+6 direct magic damage.` },
      { name: `Nightmare Tableau`, type: `Action`, text: `Mark a Stress to trap a target within Far range in a powerful illusion of their worst fears. While trapped, the target is Restrained and Vulnerable until they break free, ending both conditions, with a successful Instinct Roll.` },
      { name: `Slippery`, type: `Reaction`, text: `When the Sorcerer takes damage from an attack, they can teleport up to Far range.` },
      { name: `Shackles of Guilt`, type: `Reaction`, text: `Countdown (Loop 2d6). When the Sorcerer is in the spotlight for the first time, activate the countdown. When it triggers, all targets within Far range become Vulnerable and must mark a Stress as they relive their greatest regrets. A target can break free from their regret with a successful Presence or Strength Roll. When a PC fails to break free, they lose a Hope.` },
    ],
  },
  {
    name: `Fallen Warlord: Realm-Breaker`,
    tier: 4,
    role: `Solo`,
    description: `A Fallen God, wreathed in rage and resentment, bearing millennia of experience in breaking heroes' spirits.`,
    motives: `Corrupt, dominate, punish, break the weak`,
    difficulty: 20,
    thresholds: { major: `36`, severe: `66` },
    hp: 8,
    stress: 5,
    atk: { modifier: `+7`, name: `Barbed Whip`, range: `Close`, damage: `4d8+7 phy` },
    experience: `Conquest +3, History +2, Intimidation +3`,
    features: [
      { name: `Relentless (2)`, type: `Passive`, text: `The Realm-Breaker can be spotlighted up to two times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Firespite Plate Armor`, type: `Passive`, text: `When the Realm-Breaker takes damage, reduce it by 2d10.` },
      { name: `Tormenting Lash`, type: `Action`, text: `Mark a Stress to make a standard attack against all targets within Very Close range. When a target uses armor to reduce damage from this attack, they must mark 2 Armor Slots.` },
      { name: `All-Consuming Rage`, type: `Reaction`, text: `Countdown (Decreasing 8). When the Realm-Breaker is in the spotlight for the first time, activate the countdown. When it triggers, create a torrent of incarnate rage that rends flesh from bone. All targets within Far range must make a Presence Reaction Roll. Targets who fail take 2d6+10 direct magic damage. Targets who succeed take half damage. For each HP marked from this damage, summon a Fallen Shock Troop within Very Close range of the target who marked that HP.` },
      { name: `Doombringer`, type: `Reaction`, text: `When a target marks HP from an attack by the Realm-Breaker, all PCs within Far range of the target must lose a Hope.` },
      { name: `I Have Never Known Defeat (Phase Change)`, type: `Reaction`, text: `When the Realm-Breaker marks their last HP, replace them with the Undefeated Champion and immediately spotlight them.` },
    ],
  },
  {
    name: `Zombie Legion`,
    tier: 4,
    role: `Horde`,
    roleSubtype: `3/HP`,
    description: `A large pack of undead, still powerful despite their rotting flesh.`,
    motives: `Consume brain, shred flesh, surround`,
    difficulty: 17,
    thresholds: { major: `25`, severe: `45` },
    hp: 8,
    stress: 5,
    atk: { modifier: `+2`, name: `Undead Hands`, range: `Close`, damage: `4d6+10 phy` },
    features: [
      { name: `Horde (2d6+5)`, type: `Passive`, text: `When the Legion has marked half or more of their HP, their standard attack deals 2d6+5 physical damage instead.` },
      { name: `Unyielding`, type: `Passive`, text: `The Legion has resistance to physical damage.` },
      { name: `Relentless (2)`, type: `Passive`, text: `The Legion can be spotlighted up to two times per GM turn. Spend Fear as usual to spotlight them.` },
      { name: `Overwhelm`, type: `Reaction`, text: `When the Legion takes Minor damage from an attack within Melee range, you can mark a Stress to make a standard attack with advantage against the attacker.` },
    ],
  },
];
